import React, { useState, useEffect } from 'react';
import { useAuth, useFirebase } from '../context/AuthContext';
import { collection, onSnapshot, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { ThumbsUp } from 'lucide-react';

const ReviewSuggestions = () => {
    const { user } = useAuth();
    const { db } = useFirebase();
    const [suggestions, setSuggestions] = useState([]);

    useEffect(() => {
        if (!db) return;
        const suggestionsRef = collection(db, 'suggestions');
        const unsubscribe = onSnapshot(suggestionsRef, (snapshot) => {
            const fetchedSuggestions = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(), 
                timestamp: doc.data().timestamp ? doc.data().timestamp.toDate() : null 
            }));
            setSuggestions(fetchedSuggestions.sort((a, b) => (b.upvotes?.length || 0) - (a.upvotes?.length || 0) || b.timestamp - a.timestamp));
        });
        return () => unsubscribe();
    }, [db]);

    const handleUpvote = async (suggestionId) => {
        if (!db || !user) {
          console.error("Database or user session not available.");
          return;
        }
        const suggestionRef = doc(db, 'suggestions', suggestionId);
        const suggestion = suggestions.find(s => s.id === suggestionId);
        const hasUpvoted = suggestion?.upvotes?.includes(user.uid);
        try {
            await updateDoc(suggestionRef, {
                upvotes: hasUpvoted ? arrayRemove(user.uid) : arrayUnion(user.uid)
            });
        } catch (error) {
            console.error("Error updating upvote:", error);
            alert("Could not register vote.");
        }
    };

    return (
        <div className="page-content p-4 md:p-6">
          <div className="text-center mb-8">
              <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-2">Review Suggestions</h1>
              <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg">View and upvote suggestions submitted by the community.</p>
          </div>
          <div className="space-y-4">
              {suggestions.length > 0 ? (suggestions.map((suggestion) => {
                  const hasUpvoted = suggestion.upvotes && suggestion.upvotes.includes(user?.uid);
                  const upvoteCount = suggestion.upvotes ? suggestion.upvotes.length : 0;
                  
                  return (
                    <div key={suggestion.id} className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-300 dark:border-gray-700">
                      <p className="text-gray-700 dark:text-gray-300 italic mb-2">"{suggestion.suggestionText}"</p>
                      <div className="text-sm text-gray-500 dark:text-gray-500 mt-4 border-t border-gray-300 dark:border-gray-700 pt-2 flex justify-between items-center">
                        <div>
                          <p>Submitted by: <span className="font-mono text-xs text-gray-600 dark:text-gray-400 break-all">{suggestion.userId}</span></p>
                          <p>Date: {suggestion.timestamp ? suggestion.timestamp.toLocaleString() : 'N/A'}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => handleUpvote(suggestion.id)}
                                className={`flex items-center space-x-1 p-2 rounded-lg transition-colors duration-200 ${hasUpvoted ? 'bg-blue-600 text-white' : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'}`}
                                aria-label="Upvote suggestion"
                            >
                                <ThumbsUp size={18} className={hasUpvoted ? 'fill-current' : ''} />
                            </button>
                            <span className="font-bold text-lg text-gray-700 dark:text-gray-300">{upvoteCount}</span>
                        </div>
                      </div>
                     </div>
                  );
                })) : (<div className="text-center text-gray-500 dark:text-gray-500 py-8">No suggestions have been submitted yet.</div>)
            }
          </div>
        </div>
    );
};

export default ReviewSuggestions;